"use client";

/**
 * The concrete generative-UI renderer chat installs (tdd-0059 Phase 5).
 *
 * `ComponentSegment` takes its renderer by injection so the transcript stays
 * decoupled from any particular rendering stack. This is the implementation
 * that gets injected: it compiles the JSONL from a fenced block into a spec and
 * hands it to `@workspace/gen-ui`.
 *
 * Form state is held here, per block, keyed by element id. A generated form's
 * fields bind by path into that state, and `onRenderAction` delivers the
 * collected values on submit — so this component owns the whole round trip for
 * everything the transcript renders.
 */

import { lazy, Suspense } from "react";
import type { ComponentRenderer } from "./component-segment";

/**
 * Loaded on demand — see gen-ui-block.tsx for why the boundary exists.
 *
 * `lazy` needs a default export, which is why that module has one. The import
 * must stay dynamic: making it static re-merges the catalog into the entry
 * chunk and silently costs ~261KB gzipped before hydration.
 */
const GenUIBlock = lazy(() => import("./gen-ui-block"));

export function createGenUIRenderer(
  onSubmit?: (
    formId: string,
    values: Record<string, unknown>,
  ) => void | Promise<void>,
  /**
   * Opens the host's file picker when the model renders an attachment request.
   * Omit and those components render but their button does nothing.
   */
  onRequestAttachment?: (mode: "images" | "files" | "any") => void,
  /**
   * The model rendered a LocationRequest and the user pressed Share. The host
   * owns the geolocation permission, so the package only publishes the intent.
   */
  onRequestLocation?: (reason?: string, requestId?: string) => void,
): ComponentRenderer {
  return {
    render: (jsonl, { partial }) => {
      // A partial block is handled upstream by ComponentSegment; guard anyway
      // so a caller can't compile a half-written spec by mistake.
      if (partial) return null;
      return (
        // No fallback: a block appears mid-stream in an already-painted
        // transcript, and a skeleton flashing in for one chunk fetch reads as
        // a glitch rather than as progress.
        <Suspense fallback={null}>
          <GenUIBlock
            jsonl={jsonl}
            onSubmit={onSubmit}
            onRequestAttachment={onRequestAttachment}
            onRequestLocation={onRequestLocation}
          />
        </Suspense>
      );
    },
  };
}
